import { fichaFuncion } from '@/components/asistente/catalogoFuncionesE1';
import IconoTrazo, { TRAZO_CHECK } from '@/components/marketing/comun/IconoTrazo';
import { MARCO_ASISTENTE } from '@/components/marketing/comun/textosAsistenteEjemplo';
import {
    EJEMPLOS_PODERES,
    type EjemploPoder,
    type FilaEjemplo,
    type IdFuncionPoder,
    type TonoFilaEjemplo,
    type VarianteChipEjemplo,
} from '@/content/ejemplosPoderes';

// Ilustración, no un chat: no tiene estado ni nada pulsable, así que se pinta en el servidor (el
// héroe de la página de Poderes) y dentro de la sección cliente sin cambios. El nombre de la función
// sale del catálogo (C-5) y la conversación de EJEMPLOS_PODERES (D-22). Los tonos del lienzo llegan
// como nombres y se traducen a tokens solo aquí.

interface PropiedadesPoderesDemo {
    funcion: IdFuncionPoder;
    ancho: number;
    alto: number;
}

/** Por debajo de este ancho la cabecera no cabe con el chip y el chip abre el cuerpo (Poderes_Demo `ancha`). */
const ANCHO_CHIP_EN_CABECERA = 400;

const TONOS_FILA: Readonly<Record<TonoFilaEjemplo, { fila: string; valor: string }>> = {
    ok: { fila: 'bg-surface-2', valor: 'text-primary' },
    neutro: { fila: 'bg-surface-2', valor: 'text-on-surface-variant' },
    dudosa: { fila: 'bg-tint-warning-strong', valor: 'text-on-tint-warning' },
};

const TONOS_CHIP: Readonly<Record<VarianteChipEjemplo, string>> = {
    acento: 'bg-tint-accent border-accent-blue text-primary',
    notasFijas: 'bg-tint-brand-highlight-strong border-warning-suave text-on-tint-warning',
};

function datosDemo(funcion: IdFuncionPoder): { nombre: string; ejemplo: EjemploPoder } {
    const ficha = fichaFuncion(funcion);
    const ejemplo = Object.hasOwn(EJEMPLOS_PODERES, funcion) ? EJEMPLOS_PODERES[funcion] : undefined;
    if (!ficha || !ejemplo) {
        throw new Error(`PoderesDemo: la función «${funcion}» no está en el catálogo del asistente o no tiene ejemplo`);
    }
    return { nombre: ficha.nombre, ejemplo };
}

function ChipFuncion({ nombre, className }: { nombre: string; className?: string }) {
    return (
        <span
            data-parte="chip-funcion"
            className={`shrink-0 rounded-full bg-tint-accent px-[10px] py-[3px] text-[12px] font-bold text-primary ${className ?? ''}`}
        >
            {nombre}
        </span>
    );
}

function Cabecera({ nombre, ancha }: { nombre: string; ancha: boolean }) {
    return (
        <div data-parte="cabecera" className="flex h-[52px] shrink-0 items-center gap-[10px] border-b border-surface-3 px-[14px]">
            <span className="flex size-[30px] shrink-0 items-center justify-center rounded-[9px] bg-accent-blue text-ink">
                <IconoTrazo trazo="M2.5 3.5h11v7.5H7l-3 2.5V11H2.5z" tamano={16} grosor={1.6} />
            </span>
            <span className="flex min-w-0 grow flex-col">
                <strong className="font-display text-[15px] leading-[1.2]">{MARCO_ASISTENTE.nombre}</strong>
                <span className="text-[12px] text-on-surface-variant">{MARCO_ASISTENTE.alcance}</span>
            </span>
            {ancha && <ChipFuncion nombre={nombre} />}
        </div>
    );
}

function Adjunto({ nombre }: { nombre: string }) {
    return (
        <span
            data-parte="adjunto"
            className="flex items-center gap-ds-2 self-end rounded-[10px] border-[1.5px] border-ink bg-surface-suave px-[10px] py-[6px] text-[13px] font-semibold"
        >
            <IconoTrazo trazo="M4 1.5h5l3 3v10H4z M9 1.5v3h3" tamano={14} grosor={1.6} className="text-primary" />
            {nombre}
        </span>
    );
}

function Respuesta({ texto, cita }: { texto: string; cita?: string }) {
    return (
        <p className="m-0 leading-[22px]">
            {texto}
            {cita && (
                <>
                    {' '}
                    <span
                        data-parte="cita"
                        className="inline-block rounded-[6px] border-2 border-ink bg-accent-blue px-[5px] text-[12px] font-bold leading-4 text-on-accent-blue"
                    >
                        {cita}
                    </span>
                </>
            )}
        </p>
    );
}

function Fila({ fila }: { fila: FilaEjemplo }) {
    const tono = TONOS_FILA[fila.tono];
    return (
        <div
            data-parte="fila"
            className={`flex items-center justify-between gap-[10px] border-t border-surface-1 px-3 py-[7px] text-[13px] ${tono.fila}`}
        >
            {/* El lienzo deja el valor sin encoger y la etiqueta con min-w-0: a 358 px «Antecedentes» quedaba
                debajo de su valor largo. El valor se queda entero hasta el 60 % y, si es más largo, parte en dos
                líneas; la etiqueta nunca baja de su palabra más larga. */}
            <span>{fila.etiqueta}</span>
            <span className={`max-w-[60%] shrink-0 text-right font-semibold ${tono.valor}`}>{fila.valor}</span>
        </div>
    );
}

function Tabla({ ejemplo }: { ejemplo: EjemploPoder }) {
    return (
        <div data-parte="tabla" className="shrink-0 overflow-hidden rounded-[14px] border-2 border-ink">
            {ejemplo.cabecera && (
                <div className="flex flex-col gap-px border-b border-surface-3 px-3 py-[9px]">
                    <strong className="leading-[1.25]">{ejemplo.cabecera}</strong>
                    <span className="text-[12px] text-on-surface-variant">{ejemplo.subcabecera}</span>
                </div>
            )}
            {ejemplo.filas?.map((fila) => <Fila key={fila.etiqueta} fila={fila} />)}
        </div>
    );
}

// Aprobar va en relleno de acento; Editar y Rechazar, en blanco.
const ACCIONES_PROPUESTA = MARCO_ASISTENTE.botonesPropuesta.map((texto, i) => ({
    texto,
    clases: i === 0 ? 'bg-accent-blue text-on-accent-blue font-bold' : 'bg-surface-2 font-semibold',
}));

function Propuesta({ texto }: { texto: string }) {
    return (
        <div
            data-parte="propuesta"
            className="flex shrink-0 flex-col gap-[7px] rounded-[14px] border-[1.5px] border-dashed border-brand-600 bg-surface-suave px-3 py-[10px]"
        >
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-primary">
                {MARCO_ASISTENTE.propuestaPendiente}
            </span>
            <strong className="leading-[1.3]">{texto}</strong>
            <div className="flex gap-[6px]">
                {ACCIONES_PROPUESTA.map((accion) => (
                    <span
                        key={accion.texto}
                        data-parte="accion"
                        className={`flex h-8 items-center rounded-[9px] border-2 border-ink px-3 text-[13px] ${accion.clases}`}
                    >
                        {accion.texto}
                    </span>
                ))}
            </div>
        </div>
    );
}

function ChipResultado({ texto, variante }: { texto: string; variante: VarianteChipEjemplo }) {
    return (
        <span
            data-parte="chip-resultado"
            className={`flex h-[30px] items-center gap-[6px] self-start rounded-full border px-3 text-[13px] font-bold ${TONOS_CHIP[variante]}`}
        >
            {texto}
        </span>
    );
}

function Asistente({ ejemplo }: { ejemplo: EjemploPoder }) {
    return (
        <div className="flex min-h-0 gap-ds-2">
            <span className="size-[22px] shrink-0 rounded-[7px] bg-primary" />
            <div className="flex min-w-0 grow flex-col gap-ds-2">
                {ejemplo.texto && <Respuesta texto={ejemplo.texto} cita={ejemplo.cita} />}
                {ejemplo.filas && <Tabla ejemplo={ejemplo} />}
                {ejemplo.propuesta && <Propuesta texto={ejemplo.propuesta} />}
                {ejemplo.chip && <ChipResultado texto={ejemplo.chip.texto} variante={ejemplo.chip.variante} />}
                <span data-parte="nota" className="text-[12px] text-on-surface-variant">
                    {ejemplo.nota}
                </span>
            </div>
        </div>
    );
}

function Cuerpo({ nombre, ejemplo, ancha }: { nombre: string; ejemplo: EjemploPoder; ancha: boolean }) {
    return (
        <div data-parte="cuerpo" className="flex min-h-0 grow flex-col gap-[10px] overflow-hidden p-[14px]">
            {!ancha && <ChipFuncion nombre={nombre} className="self-start" />}
            {ejemplo.adjunto && <Adjunto nombre={ejemplo.adjunto} />}
            <div
                data-parte="pregunta"
                className="max-w-[88%] self-end rounded-[14px_14px_4px_14px] bg-tint-accent-strong px-3 py-ds-2"
            >
                {ejemplo.pregunta}
            </div>
            <span className="flex items-center gap-ds-2 text-[12px] font-semibold text-on-surface-variant">
                <IconoTrazo trazo={TRAZO_CHECK} tamano={13} grosor={2} className="text-primary" />
                {ejemplo.paso}
            </span>
            <Asistente ejemplo={ejemplo} />
        </div>
    );
}

function IconoCuadricula() {
    return (
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
            {[2, 9].flatMap((y) => [2, 9].map((x) => <rect key={`${x}-${y}`} x={x} y={y} width="5" height="5" rx="1" />))}
        </svg>
    );
}

function Compositor() {
    return (
        <div className="shrink-0 border-t border-surface-3 px-[10px] pb-[10px] pt-ds-2">
            <div className="flex h-11 items-center gap-ds-2 rounded-[13px] border border-surface-4 bg-surface-1 px-[5px]">
                <span className="flex h-[34px] shrink-0 items-center gap-[6px] rounded-[10px] bg-tint-accent-strong px-[9px] text-[12px] font-bold text-primary">
                    <IconoCuadricula />
                    {MARCO_ASISTENTE.funciones}
                </span>
                <span className="min-w-0 grow overflow-hidden whitespace-nowrap text-[13px] text-on-surface-variant">
                    {MARCO_ASISTENTE.marcador}
                </span>
                <span className="flex size-[34px] shrink-0 items-center justify-center rounded-[10px] border-2 border-ink bg-accent-blue text-ink">
                    <IconoTrazo trazo="M8 13V3M4 7l4-4 4 4" tamano={15} grosor={1.8} />
                </span>
            </div>
        </div>
    );
}

export default function PoderesDemo({ funcion, ancho, alto }: PropiedadesPoderesDemo) {
    const { nombre, ejemplo } = datosDemo(funcion);
    const ancha = ancho >= ANCHO_CHIP_EN_CABECERA;
    const etiqueta = `Ejemplo del asistente, función ${nombre}: le piden «${ejemplo.pregunta}» y responde con datos ficticios de la paciente de ejemplo.`;
    return (
        <div
            role="img"
            data-nosnippet
            aria-label={etiqueta}
            style={{ width: ancho, height: alto }}
            className="flex max-w-full flex-col overflow-hidden rounded-[20px] border-2 border-ink bg-surface-2 font-sans text-[14px] leading-[1.45] text-ink shadow-ds-3"
        >
            <Cabecera nombre={nombre} ancha={ancha} />
            <Cuerpo nombre={nombre} ejemplo={ejemplo} ancha={ancha} />
            <Compositor />
        </div>
    );
}
